import {Fragment, ReactNode} from "react";
import {tokenClasses} from "./message-styles";
import type {MessageToken, ObjectRef, RichMessage} from "./types";

/** Plain, named text for accessible labels and layout estimates; never exposes token link IDs. */
export const richMessagePlainText = (message: RichMessage, expanded = false): string => {
  const template = (expanded && message.detail) || message.summary || message.plainText || "";
  const tokens = new Map((message.tokens ?? []).map(token => [token.key, token]));
  return template.replace(/\{([^{}]+)\}/g, (placeholder, key: string) => {
    const token = tokens.get(key);
    return token ? `${token.display ?? ""}${token.unit ? ` ${token.unit}` : ""}` : placeholder;
  });
};

export type RichMessageTextProps = {
  message?: RichMessage;
  /** Render `detail` when the message has one, rather than `summary`. */
  expanded?: boolean;
  /** Given a token's link, whether this screen can actually reach it. */
  canNavigate?: (ref: ObjectRef) => boolean;
  onNavigate?: (ref: ObjectRef) => void;
  className?: string;
};

const PLACEHOLDER = /\{([^{}]+)\}/g;

/**
 * Renders a server-authored message: a template, plus tokens, plus no markup.
 *
 * Token values are rendered as React text nodes, so a site name containing angle brackets is text
 * and never markup. That safety is structural rather than a matter of remembering to escape.
 *
 * An unmatched placeholder is emitted verbatim rather than blanked -- a visible `{foo}` is a bug
 * report, a silent gap is not.
 */
export const RichMessageText = ({
  message,
  expanded = false,
  canNavigate,
  onNavigate,
  className
}: RichMessageTextProps) => {
  if (!message) {
    return null;
  }

  const template = (expanded && message.detail) || message.summary || message.plainText || "";
  const byKey = new Map<string, MessageToken>();
  for (const token of message.tokens ?? []) {
    if (token.key) {
      byKey.set(token.key, token);
    }
  }

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  PLACEHOLDER.lastIndex = 0;
  while ((match = PLACEHOLDER.exec(template)) !== null) {
    const token = byKey.get(match[1]);
    if (!token) {
      continue;
    }

    if (match.index > lastIndex) {
      parts.push(<Fragment key={`t${lastIndex}`}>{template.slice(lastIndex, match.index)}</Fragment>);
    }

    parts.push(
      <TokenSpan
        key={`k${match.index}`}
        token={token}
        canNavigate={canNavigate}
        onNavigate={onNavigate}/>
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < template.length) {
    parts.push(<Fragment key={`t${lastIndex}`}>{template.slice(lastIndex)}</Fragment>);
  }

  return <span className={className}>{parts}</span>;
};

export type TokenSpanProps = {
  token?: MessageToken;
  canNavigate?: (ref: ObjectRef) => boolean;
  onNavigate?: (ref: ObjectRef) => void;
};

/** One token. Exported so fact rows render identically to tokens inside a sentence. */
export const TokenSpan = ({token, canNavigate, onNavigate}: TokenSpanProps) => {
  if (!token) {
    return null;
  }

  const link = token.link;
  // A link is only offered when the host says it can be followed. A dead link that looks live is
  // worse than plain text.
  const clickable = Boolean(link && onNavigate && (!canNavigate || canNavigate(link)));

  const content = (
    <>
      {token.display ?? ""}
      {token.unit ? <span className="ml-0.5 text-[0.9em] font-normal text-brand-600 dark:text-brand-300">{token.unit}</span> : null}
    </>
  );

  if (!clickable) {
    return <span className={tokenClasses(token, false)} title={token.hint}>{content}</span>;
  }

  return (
    <button
      type="button"
      title={token.hint}
      className={tokenClasses(token, true)}
      onClick={(event) => {
        // The row behind this token has its own click target; only one of them should fire.
        event.stopPropagation();
        onNavigate?.(link as ObjectRef);
      }}>
      {content}
    </button>
  );
};
